import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  PRIMARY_GENRES, MOODS, TEMPOS, LANGUAGES, SONG_LENGTHS, SONG_STRUCTURES,
  VOCALISTS, VOICE_TYPES, DELIVERIES,
} from "../data/studio";

const CUSTOM = "__custom__";

function SmartSelect({ testId, label, value, options, placeholder, onChange }) {
  // If current value isn't in options (and not empty), treat as custom text
  const inList = value === "" || options.includes(value);
  const selectValue = value === "" ? "" : inList ? value : CUSTOM;

  return (
    <div className="space-y-1.5">
      <Label className="font-label text-amber-400/90">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => onChange(v === CUSTOM ? " " : v)}
      >
        <SelectTrigger
          data-testid={testId}
          className="h-11 bg-[#0D1424] border-[#1E2C48] text-slate-100 focus:ring-amber-400/40"
        >
          <SelectValue placeholder={placeholder || "Select…"} />
        </SelectTrigger>
        <SelectContent className="bg-[#0D1424] border-[#1E2C48] text-slate-100 max-h-72">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="focus:bg-amber-400/10 focus:text-amber-100">
              {o}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM} className="focus:bg-amber-400/10 text-amber-200">
            ✎ Custom…
          </SelectItem>
        </SelectContent>
      </Select>
      {!inList && value !== "" && (
        <Input
          data-testid={`${testId}-custom`}
          autoFocus
          value={value.trim() === "" ? "" : value}
          placeholder="Type your own…"
          onChange={(e) => onChange(e.target.value)}
          className="h-10 bg-[#121B30] border-[#1E2C48] text-slate-100"
        />
      )}
    </div>
  );
}

function TextField({ testId, label, value, placeholder, onChange, area }) {
  const Comp = area ? Textarea : Input;
  return (
    <div className="space-y-1.5">
      <Label className="font-label text-amber-400/90">{label}</Label>
      <Comp
        data-testid={testId}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`bg-[#0D1424] border-[#1E2C48] text-slate-100 placeholder:text-slate-500 focus-visible:ring-amber-400/40 ${
          area ? "min-h-[76px]" : "h-11"
        }`}
      />
    </div>
  );
}

export function SongControlsForm({ selections, onChange }) {
  const set = (key) => (val) => onChange({ ...selections, [key]: val });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SmartSelect testId="field-primaryGenre" label="Primary Genre" value={selections.primaryGenre}
          options={PRIMARY_GENRES} onChange={set("primaryGenre")} />
        <SmartSelect testId="field-secondaryGenre" label="Second Genre (optional)" value={selections.secondaryGenre}
          options={PRIMARY_GENRES} onChange={set("secondaryGenre")} placeholder="Optional…" />
        <SmartSelect testId="field-mood" label="Mood" value={selections.mood}
          options={MOODS} onChange={set("mood")} />
        <SmartSelect testId="field-tempo" label="Tempo" value={selections.tempo}
          options={TEMPOS} onChange={set("tempo")} />
      </div>

      <TextField testId="field-instruments" label="Instruments" area
        value={selections.instruments} onChange={set("instruments")}
        placeholder="E.g. Fingerpicked acoustic guitar, Rhodes, warm kick drum" />
      <TextField testId="field-productionSound" label="Production Sound" area
        value={selections.productionSound} onChange={set("productionSound")}
        placeholder="E.g. Analog tape warmth, intimate dry vocals, lush reverb" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField testId="field-songTopic" label="Song Topic / Story"
          value={selections.songTopic} onChange={set("songTopic")}
          placeholder="The narrative core or life event" />
        <TextField testId="field-chorusHook" label="Chorus Hook"
          value={selections.chorusHook} onChange={set("chorusHook")}
          placeholder="Key line or memorable refrain" />
        <SmartSelect testId="field-language" label="Language" value={selections.language}
          options={LANGUAGES} onChange={set("language")} />
        <SmartSelect testId="field-songLength" label="Song Length" value={selections.songLength}
          options={SONG_LENGTHS} onChange={set("songLength")} />
      </div>

      <SmartSelect testId="field-songStructure" label="Song Structure" value={selections.songStructure}
        options={SONG_STRUCTURES} onChange={set("songStructure")} />
      <TextField testId="field-transitionPoint" label="Transition Point"
        value={selections.transitionPoint} onChange={set("transitionPoint")}
        placeholder="E.g. Half-time breakdown into an explosive stadium chorus" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-[#1E2C48] bg-[#0D1424]/60 p-4">
        <div className="sm:col-span-2 font-label text-amber-400/90">Vocals</div>
        <SmartSelect testId="field-openingVocalist" label="Opening Vocalist" value={selections.openingVocalist}
          options={VOCALISTS} onChange={set("openingVocalist")} />
        <SmartSelect testId="field-openingVoiceType" label="Opening Voice Type" value={selections.openingVoiceType}
          options={VOICE_TYPES} onChange={set("openingVoiceType")} />
        <SmartSelect testId="field-laterVocalist" label="Later / Second Vocalist" value={selections.laterVocalist}
          options={VOCALISTS} onChange={set("laterVocalist")} />
        <SmartSelect testId="field-laterVoiceType" label="Later Voice Type" value={selections.laterVoiceType}
          options={VOICE_TYPES} onChange={set("laterVoiceType")} />
        <SmartSelect testId="field-delivery" label="Vocal Delivery" value={selections.delivery}
          options={DELIVERIES} onChange={set("delivery")} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div className="space-y-1.5">
          <Label className="font-label text-amber-400/90">Lyrics</Label>
          <div className="flex gap-2">
            {["Clean", "Explicit"].map((opt) => (
              <button
                key={opt}
                data-testid={`field-cleanExplicit-${opt.toLowerCase()}`}
                onClick={() => set("cleanExplicit")(opt)}
                className={`flex-1 h-11 rounded-lg border text-sm font-semibold transition-colors ${
                  selections.cleanExplicit === opt
                    ? "border-amber-400 bg-amber-400/15 text-amber-100"
                    : "border-[#1E2C48] bg-[#0D1424] text-slate-300 hover:border-amber-400/40"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center justify-between h-11 rounded-lg border border-[#1E2C48] bg-[#0D1424] px-4">
          <span className="text-sm font-medium text-slate-200">Final chorus is a duet</span>
          <Switch
            data-testid="field-finalChorusDuet"
            checked={!!selections.finalChorusDuet}
            onCheckedChange={(v) => set("finalChorusDuet")(v)}
          />
        </label>
      </div>

      <TextField testId="field-customDirectives" label="Custom Directives" area
        value={selections.customDirectives} onChange={set("customDirectives")}
        placeholder="Free-text extras (e.g. add a [Guitar Solo], mention dusty boots, tighter AABB rhymes)" />
    </div>
  );
}
